import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from "@angular/core/rxjs-interop";
import { BcEmployeeAuthFacade } from "@bayclubs/employee-auth";
import { AuthService, Menubar, MenuItem } from "@bayclubs/horizon";
import { OfficeSecurityService } from "@core/services/office-security.service";
import { CoreFacade } from "@core/store/core.facade";
import { environment } from "@environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/operators";

@Component({
    selector: 'app-menubar',
    imports: [
        Menubar
    ],
    templateUrl: './menubar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenubarComponent {

    readonly coreFacade = inject(CoreFacade);
    private readonly isMsal = environment.isMsalAuthenticationEnabled;
    private readonly auth = inject(BcEmployeeAuthFacade);
    private readonly msalAuth = this.isMsal ? inject(AuthService) : null;
    private readonly officeSecurityService = inject(OfficeSecurityService);
    private readonly translateService = inject(TranslateService);

    private readonly canManageCourts = toSignal(this.officeSecurityService.canManageCourts, { initialValue: false });
    private readonly canUseCourtBooking = toSignal(this.officeSecurityService.canUseCourtBooking, { initialValue: false });
    private readonly canViewRulesMatrix = toSignal(this.officeSecurityService.canViewRulesMatrix, { initialValue: false });

    items = computed<MenuItem[]>(() => {
        const items: MenuItem[] = [];

        if (!this.canUseCourtBooking()) {
            return items;
        }

        const clubId = this.coreFacade.selectedClubSignal()?.id;

        items.push(
            {
                label: this.translateService.instant('Navbar.CourtSheetLabel'),
                routerLink: clubId ? ['/', clubId, 'court-sheet'] : '/court-sheet'
            },
            {
                label: this.translateService.instant('Navbar.EventListLabel'),
                routerLink: clubId ? ['/', clubId, 'court-events'] : '/court-events'
            }
        );

        if (this.canManageCourts()) {
            items.push({
                label: this.translateService.instant('Navbar.CourtManagementLabel'),
                routerLink: clubId ? ['/', clubId, 'court-management'] : '/court-management'
            });
        }

        if (this.canViewRulesMatrix()) {
            items.push({
                label: this.translateService.instant('Navbar.RulesLabel'),
                routerLink: '/rules-matrix'
            });
        }

        return items;
    });

    userName = this.isMsal
        ? computed(() => this.msalAuth!.userName())
        : toSignal(
            this.auth.token$.pipe(
                map(token => {
                    const name = token?.name;
                    if (!name) return null;
                    const parts = name.trim().split(/\s+/).filter(Boolean);
                    return parts.length > 0 ? parts[0] : null;
                })
            ),
            { initialValue: null }
        );

    handleLogout(): void {
        if (this.isMsal) {
            this.msalAuth!.logoutRedirect();
        } else {
            this.auth.logOut();
        }
    }
}
